/**
 * Sorting Utility Functions
 * Client-side sorting fallback and helper functions
 */

import type { Customer } from '../../../types/customer';
import type { SortDirection, SortableColumn } from '../types/customerTable.types';

/**
 * Calculate next sort direction in cycle: null → asc → desc → null
 */
export function calculateNextDirection(
  currentColumn: SortableColumn | null,
  currentDirection: SortDirection,
  clickedColumn: SortableColumn
): SortDirection {
  // If clicking a different column, start with ascending
  if (currentColumn !== clickedColumn) {
    return 'asc';
  }

  // Cycle through: null → asc → desc → null
  if (currentDirection === null) return 'asc';
  if (currentDirection === 'asc') return 'desc';
  return null; // desc → null (clear sort)
}

/**
 * Map frontend column IDs to CustomerSortField names
 * These match the camelCase type definitions in customer.ts
 */
export function mapColumnToSortField(column: SortableColumn): string {
  const mapping: Record<SortableColumn, string> = {
    customer: 'name',
    trucks: 'truckCount',
    dueWithin2Weeks: 'trucksDueWithin2Weeks',
    dueWithin2To6Weeks: 'trucksDueWithin2To6Weeks',
    dueWithin6To12Weeks: 'trucksDueWithin6To12Weeks',
  };
  return mapping[column];
}

/** @deprecated Use mapColumnToSortField instead */
export const mapColumnToApiField = mapColumnToSortField;

/**
 * Client-side sorting fallback for offline/cached scenarios
 */
export function sortCustomersLocally(
  customers: Customer[],
  column: SortableColumn,
  direction: SortDirection
): Customer[] {
  if (!direction) return customers;

  const sorted = [...customers].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (column) {
      case 'customer':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'trucks':
        aValue = a.truckCount || 0;
        bValue = b.truckCount || 0;
        break;
      case 'dueWithin2Weeks':
        aValue = a.trucksDueWithin2Weeks || 0;
        bValue = b.trucksDueWithin2Weeks || 0;
        break;
      case 'dueWithin2To6Weeks':
        aValue = a.trucksDueWithin2To6Weeks || 0;
        bValue = b.trucksDueWithin2To6Weeks || 0;
        break;
      case 'dueWithin6To12Weeks':
        aValue = a.trucksDueWithin6To12Weeks || 0;
        bValue = b.trucksDueWithin6To12Weeks || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
}

/**
 * Get sort indicator icon based on state
 */
export function getSortIcon(
  isActive: boolean,
  direction: SortDirection
): string {
  if (!isActive || direction === null) return '⇅';
  return direction === 'asc' ? '↑' : '↓';
}

/**
 * Get ARIA label for sort button
 */
export function getSortAriaLabel(
  columnLabel: string,
  isActive: boolean,
  direction: SortDirection
): string {
  if (!isActive || direction === null) {
    return `Sort by ${columnLabel}`;
  }
  const orderText = direction === 'asc' ? 'ascending' : 'descending';
  return `Sorted by ${columnLabel} in ${orderText} order. Click to change sort order.`;
}
